import { useEffect, useRef, useState } from "react";
import DialogContent from "@mui/material/DialogContent";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { AppDialog } from "../../components/AppDialog";
import { AuthAmbientBackground, AuthScreenShell } from "./AuthScreenShell";
import { getVanillaEl } from "../../domBridge";
import { useAutolock } from "../../hooks/useAutolock";
import { appState } from "../../../state";
import { notifyStateChange } from "../../../stateBus";
import { apiFetch } from "../../../api";
import { setLockHandler, requestLock, notifyLockStateChange, type LockReason } from "../../../lockBus";
import { registerAutolockBridge } from "../../../autolockBridge";
import { refreshMediaToken, clearMediaToken } from "../../../mediaAuth";
import { validateSession, isSessionStale, forceStale } from "../../../sessionManager";
import { loadAlbums } from "../../../albumsApi";
import { loadGalleryData, loadFavoritesData } from "../../../galleryApi";
import { loadDirectory } from "../../../mentions";
import { refreshNotifications, applyNotificationCreated } from "../../../notificationsApi";
import {
  connectChatSocket,
  disconnectChatSocket,
  onSocketOpen,
  setSyncSocketHandlers,
  setOnRepeatedFailure,
} from "../../../chatSocket";
import { loadGiftsData } from "../../../giftsApi";
import { loadPresenceData } from "../../../presenceApi";
import { loadTogetherSchedulesData } from "../../../togetherApi";
import { loadCredits, loadGiftCatalog, applyCreditsUpdated, applyDailyReward } from "../../../creditsApi";
import { showToast } from "../../../toast";
import { normalizeEmail } from "../../../utils";
import type { DailyRewardResult } from "../../../types";

// Gift Experience V2's daily-connection reward - lightweight by design
// (PART 18: "do not interrupt every app launch with a large modal"), a
// richer toast (not a full sheet/dialog) for 3/7/10-day milestones is
// still just a toast, per PART 20's "do not overcomplicate V1" allowance.
function showDailyRewardToast(reward: DailyRewardResult): void {
  if (reward.bonus > 0) {
    showToast(`🔥 ${reward.streak}-day streak! +${reward.amount} credits`, "success", 6000);
  } else {
    showToast(`Welcome back — +${reward.amount} credits · ${reward.streak}-day streak`, "success", 4000);
  }
}

// The media-auth token's TTL matches the session TTL (an hour, see
// server.js's MEDIA_TOKEN_TTL_MIN) - refreshing well before that, not on
// some short "check for changes" cadence, is all this needs. This interval
// used to also double as the "poll for changes" mechanism for gallery/
// albums/notifications/gifts/presence/together-schedules, which is why it
// ran every 2 minutes - all of those moved to WS pushes (+ the onSocketOpen
// resync below for whatever happens while disconnected) further down this
// file, so nothing else needs this short a cadence anymore.
const MEDIA_TOKEN_REFRESH_MS = 45 * 60 * 1000;

type Screen = "checking" | "locked" | "resetRequired" | "unlocked";

// React port of session.ts + autolock.ts, the last two bootstrap.Modal
// consumers (unlockModal/resetPasswordModal - both data-bs-backdrop="static",
// i.e. already non-dismissible, so AppDialog with a no-op onClose reproduces
// that exactly). Highest-stakes phase in the migration plan: this gate must
// render before anything else and never throw uncaught. Unlike dom.ts's
// module-level requireEl() calls (which throw synchronously if any of ~140
// ids is missing, taking down the whole app.js bundle at import time),
// react-app.js never imports dom.ts, so this component's correctness no
// longer depends on that fragile all-or-nothing construction.
//
// startProtectedFeatures() intentionally does not call loadConversations()
// (chat.ts) or notifyUnlocked() (installPrompt.ts) - both still import
// dom.ts, so calling them directly would need a bridge. Instead they
// self-subscribe to onLockStateChange (see chat.ts/installPrompt.ts) -
// the pattern the plan calls out for features that haven't migrated yet.

// Shared by all 3 password fields (Sign In's Password, Reset's New/Confirm)
// - each instance owns its own independent show/hide state, which is
// correct: toggling one field's visibility shouldn't affect another. No
// `label` prop (placeholder instead) - MUI's NotchedOutline only draws a
// notch gap in the border when a floating label is present to compute its
// width, so omitting it entirely gives a clean unbroken pill border for
// free, without fighting MUI's floating-label internals. The real label is
// the plain <label> the caller renders above this component.
function PasswordField({
  id,
  value,
  onChange,
  inputRef,
  placeholder,
  autoComplete,
  minLength,
}: {
  id: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  inputRef?: React.Ref<HTMLInputElement>;
  placeholder: string;
  autoComplete: string;
  minLength?: number;
}) {
  const [visible, setVisible] = useState(false);
  return (
    <TextField
      id={id}
      inputRef={inputRef}
      type={visible ? "text" : "password"}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      autoComplete={autoComplete}
      required
      fullWidth
      className="auth-input"
      slotProps={{
        htmlInput: minLength ? { minLength } : undefined,
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <i className="bi bi-lock" aria-hidden="true" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label={visible ? "Hide password" : "Show password"}
                onClick={() => setVisible((v) => !v)}
                edge="end"
                size="small"
              >
                <i className={visible ? "bi bi-eye-slash" : "bi bi-eye"} aria-hidden="true" />
              </IconButton>
            </InputAdornment>
          ),
        },
      }}
    />
  );
}

export function AuthGate() {
  const [screen, setScreen] = useState<Screen>("checking");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [unlockError, setUnlockError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const passwordRef = useRef<HTMLInputElement>(null);

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetError, setResetError] = useState("");
  const [resetSubmitting, setResetSubmitting] = useState(false);
  const confirmPasswordRef = useRef<HTMLInputElement>(null);

  const autolock = useAutolock();
  const autolockRef = useRef(autolock);
  autolockRef.current = autolock;

  // Long-idle audit finding: performLock's own `if (isLockedNow()) return;`
  // guard runs synchronously, but the DOM class it's checking only flips
  // inside applyUnlocked(false), AFTER an `await apiFetch("/api/logout")`.
  // After a long idle, several sources can discover expiration around the
  // same tick (a handful of onSocketOpen resyncs 401ing together, plus the
  // new resume-validation path below) - each one's requestLock() call
  // reaches performLock and passes the stale synchronous check before any
  // of them has actually finished locking, firing several redundant
  // /api/logout calls. This flag closes that window: set synchronously
  // before the first await, so every concurrent caller after the first
  // sees it immediately and no-ops. invalidateSession (however it's
  // triggered) is idempotent as a result.
  const lockInProgressRef = useRef(false);

  function isLockedNow(): boolean {
    return getVanillaEl<HTMLDivElement>("contentWrap")?.classList.contains("fade-lock") ?? false;
  }

  function startProtectedFeatures(): void {
    if (appState.mediaTokenTimer) return;
    refreshMediaToken();
    // Music data loading is owned entirely by MusicPanel.tsx - nothing else
    // reads track data.
    loadFavoritesData();
    loadDirectory();
    connectChatSocket();
    // Albums/gallery/notifications/gifts/presence/together-schedules are
    // NOT fetched here - the onSocketOpen resync (registered in the effect
    // below) fires immediately once connectChatSocket() above actually
    // connects, covering this initial load, and again on every reconnect
    // thereafter. Same pattern togetherSync.ts/GameRoot.tsx already use for
    // their own state, generalized here instead of a blind timer.
    appState.mediaTokenTimer = setInterval(refreshMediaToken, MEDIA_TOKEN_REFRESH_MS);
  }

  function stopProtectedFeatures(): void {
    if (appState.mediaTokenTimer) {
      clearInterval(appState.mediaTokenTimer);
      appState.mediaTokenTimer = null;
    }
  }

  function applyUnlocked(unlocked: boolean): void {
    getVanillaEl<HTMLElement>("appHeader")?.classList.toggle("pre-auth-hidden", !unlocked);
    getVanillaEl<HTMLElement>("appTabBar")?.classList.toggle("is-visible", unlocked);
    const contentWrap = getVanillaEl<HTMLDivElement>("contentWrap");

    if (unlocked) {
      contentWrap?.classList.remove("fade-lock");
      contentWrap?.setAttribute("aria-hidden", "false");
      setPassword("");
      setUnlockError("");
      setScreen("unlocked");
      startProtectedFeatures();
      autolockRef.current.armAutolock();
      autolockRef.current.resetInactivityTimer();
      notifyLockStateChange("unlocked");
      // Allow a future lock to proceed - see lockInProgressRef's comment.
      lockInProgressRef.current = false;
    } else {
      contentWrap?.classList.add("fade-lock");
      contentWrap?.setAttribute("aria-hidden", "true");
      setScreen("locked");
      setTimeout(() => passwordRef.current?.focus(), 250);
      stopProtectedFeatures();
      autolockRef.current.disarmAutolock();
      clearMediaToken();
      disconnectChatSocket();

      // Locking is supposed to hide your content - the fullscreen lightbox
      // is a separate top-level element that fade-lock on #contentWrap
      // never touches, so without this a photo/video left open stays
      // visibly open (and playing) right through the lock screen.
      appState.currentMediaIndex = -1;
      notifyStateChange();
      (document.getElementById("mediaModalVideo") as HTMLVideoElement | null)?.pause();
      (document.getElementById("audioPlayer") as HTMLAudioElement | null)?.pause();
      notifyLockStateChange("locked");
    }
  }

  useEffect(() => {
    registerAutolockBridge({
      pauseInactivityForMedia: autolock.pauseInactivityForMedia,
      resumeInactivityAfterMedia: autolock.resumeInactivityAfterMedia,
      resetInactivityTimer: autolock.resetInactivityTimer,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Registered once for the app's whole lifetime (like GiftsScreen.tsx's
  // setGiftSocketHandlers, togetherSync.ts's onSocketOpen) rather than only
  // while unlocked - harmless before connectChatSocket() ever runs, since
  // no frames/opens arrive until it does. onSocketOpen fires on every
  // (re)connect, including the very first, so this covers both this app's
  // initial load AND catching up on anything missed while disconnected -
  // deliberately unconditional, not gated behind "only if I already have
  // local state", same bug class fixed twice before elsewhere in this app.
  useEffect(() => {
    setSyncSocketHandlers({
      onNotificationCreated: (data) => applyNotificationCreated(data.notification),
      onAlbumsChanged: () => loadAlbums(),
      onMediaChanged: () => loadGalleryData(),
      onCreditsUpdated: (data) => applyCreditsUpdated(data.balance),
    });
    return onSocketOpen(() => {
      refreshNotifications();
      loadAlbums();
      loadGalleryData();
      loadGiftsData();
      loadPresenceData();
      loadTogetherSchedulesData();
      loadCredits();
      loadGiftCatalog();
    });
  }, []);

  useEffect(() => {
    async function performLock(reason: LockReason): Promise<void> {
      if (isLockedNow() || lockInProgressRef.current) return;
      // Set BEFORE the first await - see lockInProgressRef's own comment on
      // why the isLockedNow() check alone isn't enough to make this
      // idempotent under concurrent callers.
      lockInProgressRef.current = true;
      try {
        await apiFetch("/api/logout", { method: "POST" });
      } catch {
        // ignore
      }
      // Session-dependent state, cleared so a locked screen (or the next
      // person to unlock, on a shared 2-account device) never shows stale
      // content from the previous session. disconnectChatSocket() (called
      // inside applyUnlocked(false) below) has already torn down the WS by
      // the time any of this runs, so an in-progress Together/Game/Party/
      // Wheel session here is already orphaned either way - nulling it
      // clears the otherwise-stale "still watching together" UI rather
      // than leaving it stuck on screen with a dead connection behind it.
      // Deliberately NOT cleared: viewMode/sortState/searchQuery/theme -
      // harmless UI preferences, not session data.
      appState.currentUserEmail = "";
      appState.currentUserFullName = "";
      appState.currentUserAvatarFilename = null;
      appState.albums = [];
      appState.media = [];
      appState.favoritesSet = new Set();
      appState.chatConversations = [];
      appState.chatThreadMessages = [];
      appState.chatThreadId = null;
      appState.gifts = [];
      appState.giftCatalog = [];
      appState.credits = 0;
      appState.presenceUsers = [];
      appState.togetherSession = null;
      appState.togetherAwaitingRejoin = false;
      appState.togetherIncomingInvite = null;
      appState.gameSession = null;
      appState.gameIncomingInvite = null;
      appState.partySession = null;
      appState.partyIncomingInvite = null;
      appState.wheelSession = null;
      appState.wheelIncomingInvite = null;
      applyUnlocked(false);
      console.log(`[SESSION] reset completed, reason=${reason}`);
    }
    setLockHandler(performLock);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const logoutBtn = getVanillaEl<HTMLButtonElement>("logoutBtn");
    if (!logoutBtn) return;
    const onClick = () => requestLock("manual");
    logoutBtn.addEventListener("click", onClick);
    return () => logoutBtn.removeEventListener("click", onClick);
  }, []);

  // Boot-time check - delegates to sessionManager.validateSession(), the
  // SAME primitive the resume-validation effect below reuses, so there is
  // exactly one function in the whole app that calls GET /api/me.
  useEffect(() => {
    (async () => {
      const { authenticated, mustResetPassword, dailyReward } = await validateSession();

      // The "already had a valid rolling session" boot path for the daily
      // reward - see server.js's GET /api/me comment. Applied (not just
      // toasted) immediately, since this IS the authoritative balance, not
      // a guess (PART 30).
      if (dailyReward) {
        applyDailyReward(dailyReward);
        showDailyRewardToast(dailyReward);
      }

      // A page reload with an already-valid session that never finished
      // its mandatory reset (tab closed mid-flow) - re-show the prompt
      // instead of unlocking into an app where every other API call then
      // 403s.
      if (authenticated && mustResetPassword) {
        setResetError("");
        setNewPassword("");
        setConfirmPassword("");
        setScreen("resetRequired");
        setTimeout(() => confirmPasswordRef.current?.focus(), 250);
        return;
      }

      applyUnlocked(authenticated);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Long-idle recovery: the app never re-checks its assumed auth state on
  // its own after the boot-time validation above - autolock only reacts to
  // GOING inactive/hidden, never to RETURNING. That gap is exactly what let
  // a genuinely-expired server session sit silently behind a UI that still
  // looked unlocked (see mediaAuth.ts's/chatSocket.ts's own comments for
  // the two concrete failure modes this was enabling). All three resume
  // signals funnel through the same gated path: skip entirely if already
  // locked (nothing authenticated is showing, and the unlock flow itself
  // re-validates via POST /api/unlock) or if the last validation is still
  // fresh (isSessionStale()'s 5-minute threshold - this must never turn
  // into polling), otherwise ask sessionManager for an authoritative
  // answer and recover accordingly.
  useEffect(() => {
    async function maybeRevalidate(reason: string): Promise<void> {
      if (isLockedNow()) return;
      if (!isSessionStale()) return;

      console.log(`[SESSION] resume validation triggered (${reason})`);
      const { authenticated, mustResetPassword, dailyReward } = await validateSession();

      if (!authenticated || mustResetPassword) {
        // mustResetPassword mid-session is a rare edge (an account flagged
        // remotely while this tab sat open) - re-locking and letting the
        // normal POST /api/unlock -> mustResetPassword branch in
        // handleUnlockSubmit re-surface the reset prompt is simpler and
        // just as correct as trying to jump straight to that screen from
        // here, and reuses an already-correct path instead of a second one.
        console.log("[SESSION] expired during resume - returning to auth flow");
        requestLock("session_expired");
        return;
      }

      if (dailyReward) {
        applyDailyReward(dailyReward);
        showDailyRewardToast(dailyReward);
      }

      // Session's still good - the socket may have died silently while
      // backgrounded/suspended (mobile OS suspension, a NAT timeout) with
      // no visible symptom, and the media token may have missed its
      // refresh interval entirely if it was suspended for the same reason
      // (setInterval is not reliable across a real background period - see
      // MEDIA_TOKEN_REFRESH_MS's own comment). Both are cheap/idempotent to
      // re-run regardless of whether they actually needed it.
      console.log("[SESSION] resume validation succeeded - reconnecting");
      connectChatSocket();
      refreshMediaToken();
    }

    function onVisibilityChange(): void {
      if (document.visibilityState === "visible") maybeRevalidate("visibilitychange");
    }
    function onPageShow(e: PageTransitionEvent): void {
      // event.persisted: this page was restored from the browser's
      // back/forward cache (bfcache), not a fresh load - the entire JS
      // heap, including sessionManager's own lastValidatedAt, was frozen
      // for however long that took, so a "recent" timestamp from before
      // the freeze cannot be trusted. Force a real check regardless of the
      // threshold rather than silently trusting stale in-memory state.
      if (e.persisted) {
        forceStale();
        maybeRevalidate("pageshow-bfcache");
      }
    }
    function onOnline(): void {
      maybeRevalidate("online");
    }

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pageshow", onPageShow);
    window.addEventListener("online", onOnline);
    // WS reconnect attempts that keep failing (see chatSocket.ts's
    // consecutiveFailures) funnel through this exact same gated check -
    // if the session's still valid this is just a transient hiccup and
    // reconnecting continues normally; if not, requestLock above calls
    // disconnectChatSocket() (via applyUnlocked(false)), which halts the
    // retry loop instead of it continuing forever.
    setOnRepeatedFailure(() => maybeRevalidate("ws-repeated-failure"));

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pageshow", onPageShow);
      window.removeEventListener("online", onOnline);
      setOnRepeatedFailure(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleUnlockSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    const trimmedEmail = email.trim();
    const trimmedPassword = password.trim();
    setSubmitting(true);

    try {
      // /api/unlock is CSRF-exempt server-side (by design), so normal fetch
      // is ok, but we still use apiFetch for consistency.
      const res = await apiFetch("/api/unlock", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmedEmail, password: trimmedPassword }),
      });

      if (res.ok) {
        // CSRF cookie is set by server on success
        const data = await res.json().catch(() => ({}));
        setPassword("");
        // checkSession() (below) only ever runs once, on mount, before the
        // user has logged in - a fresh login never re-fetches /api/me, so
        // without this, appState.currentUserEmail stays "" for the rest of
        // the session on this path. Every self-exclusion check that reads
        // it (Gifts'/Together Mode's recipient pickers) would then silently
        // include yourself as a valid recipient. We already know the email
        // that was just validated server-side - no extra round trip needed.
        // Normalized the same way the server stores/compares emails
        // (trim+lowercase) - the server accepts this login regardless of
        // case, but every own-message/own-gift/self-exclusion check
        // elsewhere in the app does a raw === against a server-returned,
        // already-lowercase email, so storing the raw (possibly
        // mixed-case, e.g. mobile-auto-capitalized) form input here would
        // silently break all of them for the rest of the session.
        appState.currentUserEmail = normalizeEmail(trimmedEmail);
        appState.currentUserFullName = data.fullName || "";
        appState.currentUserAvatarFilename = data.avatarFilename || null;
        notifyStateChange();
        // The "fresh login" boot path for the daily reward - see server.js's
        // POST /api/unlock comment. Deliberately shown even before a
        // pending password reset resolves (rare - only affects a freshly
        // seeded account's very first login), rather than adding a second
        // code path to defer it.
        if (data.dailyReward) {
          applyDailyReward(data.dailyReward);
          showDailyRewardToast(data.dailyReward);
        }
        if (data.mustResetPassword) {
          setResetError("");
          setNewPassword("");
          setConfirmPassword("");
          setScreen("resetRequired");
          setTimeout(() => confirmPasswordRef.current?.focus(), 250);
        } else {
          applyUnlocked(true);
        }
        return;
      }

      let msg = "Invalid email or password. Try again.";
      if (res.status === 429) msg = "Too many attempts. Wait a bit and try again.";
      setUnlockError(msg);
      passwordRef.current?.select();
    } catch {
      setUnlockError("Server error. Try again.");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleResetSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setResetError("");

    if (newPassword.length < 8) {
      setResetError("Password must be at least 8 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setResetError("Passwords don't match.");
      confirmPasswordRef.current?.select();
      return;
    }

    setResetSubmitting(true);
    try {
      const res = await apiFetch("/api/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ newPassword }),
      });

      if (res.ok) {
        setNewPassword("");
        setConfirmPassword("");
        applyUnlocked(true);
        return;
      }

      const data = await res.json().catch(() => ({}));
      setResetError(data.message || "Failed to set new password. Try again.");
    } catch {
      setResetError("Server error. Try again.");
    } finally {
      setResetSubmitting(false);
    }
  }

  return (
    <>
      {screen === "checking" && (
        <AuthAmbientBackground variant="boot">
          <div className="auth-boot" role="status" aria-live="polite">
            <span className="auth-boot-heart" aria-hidden="true">
              <span className="heart" />
            </span>
            <p className="auth-boot-label">Checking your session…</p>
          </div>
        </AuthAmbientBackground>
      )}

      <AppDialog open={screen === "locked"} onClose={() => {}}>
        <DialogContent sx={{ p: 0 }}>
          <AuthScreenShell title="Welcome back" subtitle="Sign in to continue to your private gallery">
            <div className="auth-card">
              <Typography variant="h5" className="auth-card-title" sx={{ mb: 0.5 }}>
                Sign In
              </Typography>
              <Typography variant="body2" className="auth-card-subtitle" sx={{ mb: 3 }}>
                Credentials are validated server-side. All content is encrypted at rest.
              </Typography>

              <Box component="form" onSubmit={handleUnlockSubmit} sx={{ display: "grid", gap: 2 }}>
                <div className="auth-field">
                  <label htmlFor="authEmail" className="auth-field-label">
                    Email
                  </label>
                  <TextField
                    id="authEmail"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    autoComplete="username"
                    required
                    fullWidth
                    className="auth-input"
                    // Defense in depth alongside normalizeEmail() below -
                    // mobile keyboards commonly auto-capitalize the first
                    // letter of a plain text/email input by default.
                    slotProps={{
                      htmlInput: { autoCapitalize: "none" },
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <i className="bi bi-envelope" aria-hidden="true" />
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                </div>

                <div className="auth-field">
                  <label htmlFor="authPassword" className="auth-field-label">
                    Password
                  </label>
                  <PasswordField
                    id="authPassword"
                    inputRef={passwordRef}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    autoComplete="current-password"
                  />
                </div>

                {unlockError && (
                  <Alert severity="error" role="alert">
                    {unlockError}
                  </Alert>
                )}

                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  disabled={submitting}
                  aria-label="Unlock gallery"
                  className="auth-submit-btn"
                >
                  {submitting ? (
                    <CircularProgress size={18} sx={{ color: "#fff", mr: 1.5 }} />
                  ) : (
                    <i className="bi bi-unlock-fill me-2" aria-hidden="true" style={{ marginRight: 8 }} />
                  )}
                  {submitting ? "Signing in…" : "Unlock"}
                </Button>
              </Box>
            </div>
          </AuthScreenShell>
        </DialogContent>
      </AppDialog>

      <AppDialog open={screen === "resetRequired"} onClose={() => {}}>
        <DialogContent sx={{ p: 0 }}>
          <AuthScreenShell title="Almost there" subtitle="Set your own private password to continue">
            <div className="auth-card">
              <Typography variant="h5" className="auth-card-title" sx={{ mb: 0.5 }}>
                Set a New Password
              </Typography>
              <Typography variant="body2" className="auth-card-subtitle" sx={{ mb: 3 }}>
                You signed in with a shared temporary password. Set your own private password to continue.
              </Typography>

              <Box component="form" onSubmit={handleResetSubmit} sx={{ display: "grid", gap: 2 }}>
                <div className="auth-field">
                  <label htmlFor="authNewPassword" className="auth-field-label">
                    New password
                  </label>
                  <PasswordField
                    id="authNewPassword"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="New password (min 8 characters)"
                    autoComplete="new-password"
                    minLength={8}
                  />
                </div>

                <div className="auth-field">
                  <label htmlFor="authConfirmPassword" className="auth-field-label">
                    Confirm new password
                  </label>
                  <PasswordField
                    id="authConfirmPassword"
                    inputRef={confirmPasswordRef}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    autoComplete="new-password"
                    minLength={8}
                  />
                </div>

                {resetError && (
                  <Alert severity="error" role="alert">
                    {resetError}
                  </Alert>
                )}

                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  disabled={resetSubmitting}
                  aria-label="Set new password"
                  className="auth-submit-btn"
                >
                  {resetSubmitting ? (
                    <CircularProgress size={18} sx={{ color: "#fff", mr: 1.5 }} />
                  ) : (
                    <i className="bi bi-check-lg me-2" aria-hidden="true" style={{ marginRight: 8 }} />
                  )}
                  {resetSubmitting ? "Saving…" : "Set Password"}
                </Button>
              </Box>
            </div>
          </AuthScreenShell>
        </DialogContent>
      </AppDialog>
    </>
  );
}
